import { useConfigurationGroups } from './configurationSections';

/**
 * La colonne de rubriques de /configuration.
 *
 * Extraite de ConfigurationPage parce qu'un DEUXIÈME écran l'affiche désormais :
 * le journal des événements (/journal), qui est une page reactive-json et non
 * une section de ConfigurationPage. Recopier le menu en YAML était exclu — les
 * groupes, les permissions et les icônes vivent dans `configurationSections`,
 * et deux copies auraient divergé au premier ajout de rubrique.
 *
 * Deux modes, décidés par la présence d'`onSelect` :
 *   - AVEC — on est DANS /configuration : une rubrique `id` est un onglet, donc
 *     un <button> qui bascule le panneau de droite ;
 *   - SANS — on est ailleurs : la même rubrique devient un lien vers
 *     `/configuration?tab=<id>`, faute de panneau à basculer sur place.
 *
 * Le composant est aussi enregistré comme plugin reactive-json (cf. App.jsx) :
 * appelé sans prop utile, il retombe de lui-même sur le mode lien. Ce fichier
 * n'exporte QUE lui — un export non-composant dans un .jsx casse le Fast
 * Refresh et vide l'écran au premier rechargement à chaud.
 */
export function ConfigurationSidebar({ activeSection = null, onSelect = null }) {
  const visibleGroups = useConfigurationGroups();
  const currentPath = typeof window === 'undefined' ? '' : window.location.pathname;

  return (
    <aside className="flex w-64 min-h-0 flex-shrink-0 flex-col border-r border-gray-200 bg-white">
      <div className="flex-shrink-0 border-b border-gray-200 p-5">
        <h2 className="text-lg font-bold text-gray-900">Configuration</h2>
        <p className="text-xs text-gray-500 mt-1">Paramètres globaux de l'application</p>
      </div>
      <nav className="min-h-0 flex-1 space-y-4 overflow-y-auto p-3" aria-label="Sections configuration">
        {visibleGroups.map(group => (
          <div key={group.id} className="space-y-1">
            <h3 className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              {group.label}
            </h3>
            {group.items.map(item => {
              const isTab = Boolean(item.id) && typeof onSelect === 'function';
              const href = item.href ?? `/configuration?tab=${item.id}`;
              const active = isTab ? activeSection === item.id : currentPath === (item.href ?? '');
              const className = `w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                active ? 'bg-iris-50 text-iris-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`;
              const inner = (
                <>
                  <span className={active ? 'text-iris-600' : 'text-gray-400'}>{item.icon}</span>
                  {item.label}
                </>
              );

              return isTab ? (
                <button key={item.id} type="button" aria-current={active ? 'true' : undefined}
                  onClick={() => onSelect(item.id)} className={className}>
                  {inner}
                </button>
              ) : (
                <a key={href} href={href} aria-current={active ? 'page' : undefined} className={className}>
                  {inner}
                </a>
              );
            })}
          </div>
        ))}
      </nav>
    </aside>
  );
}
